import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { fmtGHS, fmtDateTime } from "@/lib/format";
import { downloadCSV } from "@/lib/csv";
import { printDoc, esc } from "@/lib/print";
import { Undo2, RotateCcw, Download, Printer, ChevronsUpDown, Check } from "lucide-react";
import { toast } from "sonner";

const REASONS = [["wrong_item", "Wrong item"], ["damaged", "Damaged"], ["expired", "Expired"], ["adverse_reaction", "Adverse reaction"], ["customer_changed_mind", "Changed mind"]];

export default function CustomerReturns() {
  const { can } = useAuth();
  const { branchParam, allBranches } = useBranch();
  const [rows, setRows] = useState([]);
  const [orders, setOrders] = useState([]);
  const [priorByOrder, setPriorByOrder] = useState({});
  const [open, setOpen] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [qty, setQty] = useState({});
  const [method, setMethod] = useState("cash");
  const [reason, setReason] = useState("wrong_item");
  const [note, setNote] = useState("");
  const [invOpen, setInvOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/sales-returns", { params: { branch_id: branchParam } }).then((r) => setRows(r.data)).catch(() => {});
    api.get("/orders", { params: { branch_id: branchParam } }).then((r) => setOrders(r.data)).catch(() => {});
  }, [branchParam]);
  useEffect(() => { load(); }, [load]);

  const order = orders.find((o) => o.id === orderId);
  const openNew = () => { setOrderId(""); setQty({}); setMethod("cash"); setReason("wrong_item"); setNote(""); setOpen(true); };

  const pickOrder = async (id) => {
    setOrderId(id); setQty({});
    try { const { data } = await api.get("/sales-returns", { params: { order_id: id } });
      const m = {}; data.forEach((r) => r.lines.forEach((l) => { m[l.product_id] = (m[l.product_id] || 0) + l.quantity; }));
      setPriorByOrder(m);
    } catch { setPriorByOrder({}); }
  };
  const maxFor = (l) => (l.quantity || 0) - (priorByOrder[l.product_id] || 0);
  const estimate = order ? order.lines.reduce((s, l) => s + (Number(qty[l.product_id]) || 0) * (l.unit_price || 0), 0) : 0;

  const submit = async () => {
    const lines = Object.entries(qty).map(([product_id, q]) => ({ product_id, quantity: Number(q) })).filter((x) => x.quantity > 0);
    if (!orderId) return toast.error("Select an invoice");
    if (!lines.length) return toast.error("Enter at least one quantity to return");
    try {
      const idemKey = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
      const { data } = await api.post("/sales-returns", { order_id: orderId, lines, refund_method: method, reason, note }, { headers: { "Idempotency-Key": idemKey } });
      toast.success(`${data.return_no} recorded · ${data.refund_method === "credit_note" ? "credit note" : "refund"} ${fmtGHS(data.amount)}`);
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const branchName = (id) => allBranches.find((b) => b.id === id)?.name || "";

  const printCreditNote = (r) => {
    const rows2 = r.lines.map((l) => {
      return `<tr><td>${esc(l.generic_name)}</td><td class="r">${l.quantity}</td><td class="r">${fmtGHS(l.unit_price)}</td><td class="r">${fmtGHS(l.line_amount)}</td></tr>`;
    }).join("");
    const methodLabel = r.refund_method === "credit_note" ? "Credit note to account" : r.refund_method === "momo" ? "Mobile Money refund" : "Cash refund";
    const body = `<div class="row"><div><img src="${window.location.origin}/mobimed-logo.png" alt="" style="height:46px;margin-bottom:6px"/><h1>MobiMed Pharmacy</h1><h2>Credit Note ${r.return_no}</h2><div class="muted">Against invoice ${r.invoice_no}</div><div class="muted">${esc(branchName(r.branch_id))}</div></div>
      <div class="muted" style="text-align:right">${fmtDateTime(r.created_at)}<br/>${esc(r.customer_name)}<br/>${(r.customer_type || "")} customer</div></div>
      <table><thead><tr><th>Item returned</th><th class="r">Qty</th><th class="r">Unit</th><th class="r">Refund</th></tr></thead>
      <tbody>${rows2}</tbody>
      <tfoot><tr><td colspan="3" class="r">Total ${r.refund_method === "credit_note" ? "credit" : "refund"}</td><td class="r">${fmtGHS(r.amount)}</td></tr></tfoot></table>
      <div class="muted" style="margin-top:18px">Refund method: ${methodLabel} · Reason: ${(r.reason || "").replace(/_/g, " ")}${r.note ? ` · ${esc(r.note)}` : ""}<br/>Processed by ${esc(r.created_by)} · Returned stock quarantined for inspection</div>`;
    printDoc(`Credit Note ${r.return_no}`, body);
  };

  const exportCsv = () => {
    if (!rows.length) return toast.info("No returns to export");
    const flat = rows.map((r) => ({ return_no: r.return_no, invoice_no: r.invoice_no, customer: r.customer_name,
      customer_type: r.customer_type, branch: r.branch_id, items: r.item_count, amount: r.amount,
      method: r.refund_method, reason: r.reason, when: r.created_at }));
    downloadCSV("customer-returns.csv", flat, [
      { key: "return_no", label: "Return no" }, { key: "invoice_no", label: "Invoice" }, { key: "customer", label: "Customer" },
      { key: "customer_type", label: "Type" }, { key: "items", label: "Items" }, { key: "amount", label: "Amount" },
      { key: "method", label: "Refund method" }, { key: "reason", label: "Reason" }, { key: "when", label: "When" },
    ]);
  };

  return (
    <div>
      <PageHeader title="Customer Returns" subtitle="Branch supervisors process sales returns against an invoice — stock is quarantined for inspection"
        actions={<div className="flex gap-2">
          <Button variant="outline" data-testid="export-returns-btn" onClick={exportCsv}><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
          {can("sales.returns") && <Button data-testid="new-customer-return-btn" onClick={openNew}><Undo2 className="h-4 w-4 mr-1.5" /> New return</Button>}
        </div>} />

      <Card className="shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr><th className="py-2.5 px-3">Return No.</th><th className="py-2.5 px-3">Invoice</th><th className="py-2.5 px-3">Customer</th><th className="py-2.5 px-3 text-right">Items</th><th className="py-2.5 px-3 text-right">Amount</th><th className="py-2.5 px-3">Refund</th><th className="py-2.5 px-3">Reason</th><th className="py-2.5 px-3">When</th><th className="py-2.5 px-3 text-right">Credit note</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="py-10 text-center text-muted-foreground"><RotateCcw className="h-8 w-8 mx-auto mb-2 opacity-40" />No customer returns yet</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-muted/50" data-testid={`return-row-${r.return_no}`}>
                <td className="py-2 px-3 font-mono text-xs">{r.return_no}</td>
                <td className="py-2 px-3 font-mono text-xs text-sky-700">{r.invoice_no}</td>
                <td className="py-2 px-3">{r.customer_name}<span className="text-xs text-muted-foreground ml-1">{r.customer_type}</span></td>
                <td className="py-2 px-3 text-right font-mono">{r.item_count}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtGHS(r.amount)}</td>
                <td className="py-2 px-3"><StatusBadge value={r.refund_method === "credit_note" ? "on_account" : "paid"} label={r.refund_method === "credit_note" ? "credit note" : r.refund_method} /></td>
                <td className="py-2 px-3 capitalize text-muted-foreground">{(r.reason || "").replace(/_/g, " ")}</td>
                <td className="py-2 px-3 text-muted-foreground text-xs">{fmtDateTime(r.created_at)}</td>
                <td className="py-2 px-3 text-right">
                  <Button size="sm" variant="outline" data-testid={`print-credit-note-${r.return_no}`} onClick={() => printCreditNote(r)}>
                    <Printer className="h-3.5 w-3.5 mr-1.5" /> Print
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New customer return</DialogTitle><DialogDescription>Pick the invoice, choose quantities to return. Returned stock is quarantined for inspection.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Invoice</Label>
              <Popover open={invOpen} onOpenChange={setInvOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" data-testid="return-order-select" className="w-full justify-between font-normal">
                    <span className={order ? "" : "text-muted-foreground"}>
                      {order ? `${order.invoice_no} · ${order.customer_name} · ${fmtGHS(order.total)}` : "Search invoice no. or customer…"}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                  <Command filter={(value, search) => value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0}>
                    <CommandInput placeholder="Search invoice no. or customer name…" data-testid="return-order-search" />
                    <CommandList>
                      <CommandEmpty>No matching invoice</CommandEmpty>
                      <CommandGroup>
                        {orders.map((o) => (
                          <CommandItem key={o.id} value={`${o.invoice_no} ${o.customer_name}`} data-testid={`return-order-option-${o.invoice_no}`}
                            onSelect={() => { pickOrder(o.id); setInvOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 ${orderId === o.id ? "opacity-100" : "opacity-0"}`} />
                            <span className="font-mono text-xs mr-2">{o.invoice_no}</span>
                            <span className="truncate">{o.customer_name}</span>
                            <span className="ml-auto font-mono text-xs text-muted-foreground">{fmtGHS(o.total)}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            {order && (
              <div className="border-y border-border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground text-left"><tr><th className="py-2 px-2">Medicine</th><th className="py-2 px-2 text-right">Sold</th><th className="py-2 px-2 text-right">Returnable</th><th className="py-2 px-2 text-right">Return qty</th></tr></thead>
                  <tbody>
                    {order.lines.map((l) => { const mx = maxFor(l); return (
                      <tr key={l.product_id} className="border-t border-border">
                        <td className="py-1.5 px-2">{l.generic_name}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{l.quantity}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{mx}</td>
                        <td className="py-1.5 px-2 text-right"><Input type="number" min={0} max={mx} disabled={mx <= 0} className="h-8 w-20 ml-auto text-right" data-testid={`return-qty-${l.sku}`} value={qty[l.product_id] ?? ""} onChange={(e) => setQty((m) => ({ ...m, [l.product_id]: e.target.value }))} /></td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Refund method</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="return-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cash">Cash refund</SelectItem><SelectItem value="momo">Mobile Money refund</SelectItem><SelectItem value="credit_note">Credit note to account</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Reason</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger data-testid="return-reason-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Note</Label><Input data-testid="return-note-input" value={note} onChange={(e) => setNote(e.target.value)} /></div>
            <div className="flex justify-between text-sm pt-1"><span className="text-muted-foreground">Estimated {method === "credit_note" ? "credit" : "refund"}</span><span className="font-mono font-semibold">{fmtGHS(estimate)}</span></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="submit-customer-return-btn" onClick={submit}>Record return</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
